// ============================================
// FILE: services/DataService.ts
// ============================================

// Import data directly from JSON files
import studentsData from './students.json';
import quranQuestionsData from './quranquestions.json';
import quranApiData from './myquranapi.json';
import manhajData from './manhaj_new.json'; // Updated to use manhaj_new.json

// =============== TYPES ===============
export interface Student {
  nid: string;
  name_en: string;
  cls: string;
  grade: string;
  reading_mode: string;
  [key: string]: any;
}

export interface QuranQuestion {
  id: number;
  juz: number;
  sura_number: number;
  surah_name_ar: string;
  aya_number: number;
  verse_id: string;
  verse_id2: string;
  page: number;
  vid: number;
  first_verse: string;
  [key: string]: any;
}

export interface QuranVerse {
  oid: number;
  page: number;
  juz: number;
  sura_name_ar: string;
  sura_number: number;
  aya_number: number;
  aya_number_ar: string;
  verse_number: string;
  verse_text: string;
  verse_text_with_versenum: string;
  rukoou: number;
  hizb: string;
  rubu_per_hizb: string;
  total_rubu: string;
  sajidha: string;
  verse_text_full: string;
  verse_text_vn: string;
  'page-sura-verse': string;
  sv: string;
  vid: number;
  [key: string]: any;
}

export interface ManhajRange {
  category: string;
  from: number;
  to: number;
}

export interface ManhajEntry {
  id: number;
  grade: number;
  ranges: ManhajRange[];
}

export interface StudentWithManhaj extends Student {
  manhajRange?: ManhajRange;
  assignedQuestions?: QuranQuestion[];
}

// =============== DATA ACCESSORS ===============

/**
 * Get all students
 */
export const getStudents = (): Student[] => {
  try {
    return (studentsData as Student[]) || [];
  } catch (error) {
    console.error('Error loading students data:', error);
    return [];
  }
};

/**
 * Get all Quran questions
 */
export const getQuranQuestions = (): QuranQuestion[] => {
  try {
    return (quranQuestionsData as QuranQuestion[]) || [];
  } catch (error) {
    console.error('Error loading Quran questions data:', error);
    return [];
  }
};

/**
 * Get all Quran verses
 */
export const getQuranVerses = (): QuranVerse[] => {
  try {
    return (quranApiData as QuranVerse[]) || [];
  } catch (error) {
    console.error('Error loading Quran verses data:', error);
    return [];
  }
};

/**
 * Get Manhaj data
 */
export const getManhajData = (): ManhajEntry[] => {
  try {
    // Check if manhajData exists and is an array
    if (!manhajData) {
      console.warn('Manhaj data is undefined or null');
      return [];
    }
    
    if (Array.isArray(manhajData)) {
      console.log(`Loaded ${manhajData.length} manhaj entries`);
      return manhajData as ManhajEntry[];
    }
    
    console.warn('Manhaj data is not an array:', typeof manhajData);
    return [];
  } catch (error) {
    console.error('Error loading manhaj data:', error);
    return [];
  }
};

// =============== STUDENT QUERIES ===============

/**
 * Get student by NID
 */
export const getStudentByNid = (nid: string): Student | null => {
  if (!nid || nid.trim().length === 0) {
    console.warn('Invalid NID provided');
    return null;
  }

  const students = getStudents();
  const student = students.find(s => s.nid === nid);
  return student || null;
};

/**
 * Get students by grade - filters out students with empty grade
 */
export const getStudentsByGrade = (grade: string | number): Student[] => {
  if (!grade) {
    console.warn('Invalid grade provided');
    return [];
  }

  const students = getStudents();
  return students.filter(s => s.grade && s.grade === String(grade));
};

/**
 * Get students by Reading_mode
 */
export const getStudentsByReading_mode = (reading_mode: string): Student[] => {
  if (!reading_mode) {
    console.warn('Invalid reading_mode provided');
    return [];
  }

  const students = getStudents();
  return students.filter(s => s.reading_mode && s.reading_mode.toLowerCase() === reading_mode.toLowerCase());
};

/**
 * Get students by grade and reading_mode - filters out students with empty grade
 */
export const getStudentsByGradeAndReading_mode = (grade: string | number, reading_mode: string): Student[] => {
  if (!grade || !reading_mode) {
    console.warn('Grade and reading_mode are required');
    return [];
  }

  const students = getStudents();
  return students.filter(s => 
    s.grade && 
    s.grade === String(grade) && 
    s.reading_mode && 
    s.reading_mode.toLowerCase() === reading_mode.toLowerCase()
  );
};

/**
 * Search students by name
 */
export const searchStudents = (query: string): Student[] => {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const students = getStudents();
  const searchTerm = query.toLowerCase().trim();
  
  return students.filter(s => 
    (s.name_en && s.name_en.toLowerCase().includes(searchTerm)) ||
    (s.name_ar && s.name_ar?.toLowerCase().includes(searchTerm)) ||
    (s.nid && s.nid.includes(searchTerm))
  );
};

/**
 * Get all unique grades - filters out empty grades
 */
export const getAvailableGrades = (): string[] => {
  const students = getStudents();
  const grades = [...new Set(students.map(s => s.grade).filter(grade => grade && grade.trim() !== ''))];
  return grades.sort((a, b) => Number(a) - Number(b));
};

/**
 * Get all unique reading_mode values - filters out empty reading_modes
 */
export const getAvailableReading_mode = (): string[] => {
  const students = getStudents();
  const reading_modeValues = [...new Set(
    students.map(s => s.reading_mode?.toLowerCase()).filter(mode => mode && mode.trim() !== '')
  )];
  return reading_modeValues;
};

/**
 * Get students with incomplete data (missing grade or reading_mode)
 */
export const getStudentsWithIncompleteData = (): Student[] => {
  const students = getStudents();
  return students.filter(s => !s.grade || !s.reading_mode || s.grade.trim() === '' || s.reading_mode.trim() === '');
};

/**
 * Get student statistics with detailed breakdown
 */
export const getDetailedStudentStatistics = () => {
  const students = getStudents();
  const totalStudents = students.length;
  const incompleteStudents = getStudentsWithIncompleteData();
  
  // Grade distribution (excluding empty grades)
  const gradeDistribution = getAvailableGrades().map(grade => ({
    grade,
    count: students.filter(s => s.grade === grade).length,
    byReadingMode: getAvailableReading_mode().map(mode => ({
      reading_mode: mode,
      count: students.filter(s => s.grade === grade && s.reading_mode?.toLowerCase() === mode).length
    }))
  }));

  return {
    totalStudents,
    incompleteStudents: incompleteStudents.length,
    completeStudents: totalStudents - incompleteStudents.length,
    gradeDistribution,
    readingModeDistribution: getAvailableReading_mode().map(mode => ({
      reading_mode: mode,
      count: students.filter(s => s.reading_mode?.toLowerCase() === mode).length,
      byGrade: getAvailableGrades().map(grade => ({
        grade,
        count: students.filter(s => s.grade === grade && s.reading_mode?.toLowerCase() === mode).length
      }))
    }))
  };
};

// =============== MANHAJ QUERIES ===============

/**
 * Get manhaj range for a specific grade and category (reading_mode)
 */
export const getManhajRange = (grade: number, category: string): ManhajRange | null => {
  if (!grade || !category) {
    console.warn('Grade and category are required');
    return null;
  }

  try {
    const manhaj = getManhajData();
    
    // Check if manhaj is empty
    if (!manhaj || manhaj.length === 0) {
      console.warn('Manhaj data is empty');
      return null;
    }

    // Find entry for the specified grade
    const entry = manhaj.find(m => m.grade === Number(grade));
    
    if (!entry) {
      console.warn(`No manhaj found for grade ${grade}`);
      console.log('Available grades in manhaj:', manhaj.map(m => m.grade));
      return null;
    }

    // Find the range for the specified category
    const range = entry.ranges.find(r => r.category.toLowerCase() === category.toLowerCase());
    
    if (!range) {
      console.warn(`Category "${category}" not found for grade ${grade}`);
      console.log('Available categories:', entry.ranges.map(r => r.category));
      return null;
    }

    // Cache the manhaj range in localStorage
    localStorage.setItem('manhaj', `${range.from}|${range.to}`);
    localStorage.setItem('manhajStart', String(range.from));
    localStorage.setItem('manhajEnd', String(range.to));
    localStorage.setItem('manhajCategory', range.category);

    return range;
  } catch (error) {
    console.error('Error getting manhaj:', error);
    return null;
  }
};

/**
 * Get cached manhaj range from localStorage
 */
export const getCachedManhajRange = (): ManhajRange | null => {
  try {
    const start = localStorage.getItem('manhajStart');
    const end = localStorage.getItem('manhajEnd');
    const category = localStorage.getItem('manhajCategory');
    
    if (start && end && category) {
      return {
        from: Number(start),
        to: Number(end),
        category: category
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting cached manhaj:', error);
    return null;
  }
};

/**
 * Get all manhaj ranges for a specific grade
 */
export const getManhajRangesByGrade = (grade: number): ManhajRange[] => {
  if (!grade) {
    console.warn('Grade is required');
    return [];
  }

  try {
    const manhaj = getManhajData();
    const entry = manhaj.find(m => m.grade === Number(grade));
    
    if (!entry) {
      console.warn(`No manhaj found for grade ${grade}`);
      return [];
    }

    return entry.ranges;
  } catch (error) {
    console.error('Error getting manhaj ranges:', error);
    return [];
  }
};

/**
 * Get all available categories from manhaj data
 */
export const getAvailableCategories = (): string[] => {
  try {
    const manhaj = getManhajData();
    const categories = new Set<string>();
    
    manhaj.forEach(entry => {
      entry.ranges.forEach(range => {
        categories.add(range.category);
      });
    });
    
    return Array.from(categories);
  } catch (error) {
    console.error('Error getting categories:', error);
    return [];
  }
};

/**
 * Validate if a grade and reading_mode combination exists in manhaj
 */
export const isValidManhajCombination = (grade: number, reading_mode: string): boolean => {
  const range = getManhajRange(grade, reading_mode);
  return range !== null;
};

// =============== QURAN QUESTIONS QUERIES ===============

/**
 * Get questions by verse ID range
 */
export const getQuestionsByVidRange = (startVid: number, endVid: number): QuranQuestion[] => {
  if (!startVid || !endVid || startVid > endVid) {
    console.warn('Invalid verse ID range');
    return [];
  }

  const questions = getQuranQuestions();
  return questions.filter(q => q.vid >= startVid && q.vid <= endVid);
};

/**
 * Get questions by page
 */
export const getQuestionsByPage = (page: number): QuranQuestion[] => {
  if (!page || isNaN(page)) {
    console.warn('Invalid page number');
    return [];
  }

  const questions = getQuranQuestions();
  return questions.filter(q => q.page === page);
};

/**
 * Get questions by Surah
 */
export const getQuestionsBySurah = (surahNumber: number): QuranQuestion[] => {
  if (!surahNumber || isNaN(surahNumber)) {
    console.warn('Invalid surah number');
    return [];
  }

  const questions = getQuranQuestions();
  return questions.filter(q => q.sura_number === surahNumber);
};

/**
 * Get questions by Juz
 */
export const getQuestionsByJuz = (juz: number): QuranQuestion[] => {
  if (!juz || isNaN(juz)) {
    console.warn('Invalid juz number');
    return [];
  }

  const questions = getQuranQuestions();
  return questions.filter(q => q.juz === juz);
};

/**
 * Filter questions with multiple criteria
 */
export const filterQuestions = (filters: Partial<QuranQuestion>): QuranQuestion[] => {
  if (!filters || Object.keys(filters).length === 0) {
    return getQuranQuestions();
  }

  const questions = getQuranQuestions();
  return questions.filter(q => {
    return Object.entries(filters).every(([key, value]) => {
      if (value === undefined || value === null) return true;
      return q[key as keyof QuranQuestion] === value;
    });
  });
};

// =============== QURAN VERSES QUERIES ===============

/**
 * Get verses by page
 */
export const getVersesByPage = (page: number): QuranVerse[] => {
  if (!page || isNaN(page)) {
    console.warn('Invalid page number');
    return [];
  }

  const verses = getQuranVerses();
  return verses.filter(v => v.page === page);
};

/**
 * Get verse by VID
 */
export const getVerseByVid = (vid: number): QuranVerse | null => {
  if (!vid || isNaN(vid)) {
    console.warn('Invalid verse ID');
    return null;
  }

  const verses = getQuranVerses();
  return verses.find(v => v.vid === vid) || null;
};

/**
 * Get verses by Surah
 */
export const getVersesBySurah = (surahNumber: number): QuranVerse[] => {
  if (!surahNumber || isNaN(surahNumber)) {
    console.warn('Invalid surah number');
    return [];
  }

  const verses = getQuranVerses();
  return verses.filter(v => v.sura_number === surahNumber);
};

/**
 * Get verses by Juz
 */
export const getVersesByJuz = (juz: number): QuranVerse[] => {
  if (!juz || isNaN(juz)) {
    console.warn('Invalid juz number');
    return [];
  }

  const verses = getQuranVerses();
  return verses.filter(v => v.juz === juz);
};

// =============== COMBINED QUERIES ===============

/**
 * Get student with their assigned manhaj range and questions
 */
export const getStudentWithManhaj = (nid: string): StudentWithManhaj | null => {
  const student = getStudentByNid(nid);
  if (!student) return null;

  // Check if student has valid data
  if (!student.grade || !student.reading_mode) {
    console.warn(`Student ${nid} has incomplete data`);
    return {
      ...student,
      manhajRange: undefined,
      assignedQuestions: []
    };
  }

  const grade = Number(student.grade);
  const category = student.reading_mode;
  
  const manhajRange = getManhajRange(grade, category);
  
  let assignedQuestions: QuranQuestion[] = [];
  if (manhajRange) {
    assignedQuestions = getQuestionsByVidRange(manhajRange.from, manhajRange.to);
  }

  return {
    ...student,
    manhajRange: manhajRange || undefined,
    assignedQuestions
  };
};

/**
 * Get all students with their manhaj ranges (filters out students with incomplete data)
 */
export const getAllStudentsWithManhaj = (): StudentWithManhaj[] => {
  const students = getStudents();
  
  return students
    .filter(student => student.grade && student.reading_mode) // Filter out incomplete data
    .map(student => {
      const grade = Number(student.grade);
      const category = student.reading_mode;
      const manhajRange = getManhajRange(grade, category);
      
      let assignedQuestions: QuranQuestion[] = [];
      if (manhajRange) {
        assignedQuestions = getQuestionsByVidRange(manhajRange.from, manhajRange.to);
      }

      return {
        ...student,
        manhajRange: manhajRange || undefined,
        assignedQuestions
      };
    });
};

/**
 * Get questions for a specific student by NID
 */
export const getStudentQuestions = (nid: string): QuranQuestion[] => {
  const student = getStudentByNid(nid);
  if (!student) return [];

  if (!student.grade || !student.reading_mode) {
    console.warn(`Student ${nid} has incomplete data`);
    return [];
  }

  const grade = Number(student.grade);
  const category = student.reading_mode;
  const manhajRange = getManhajRange(grade, category);
  
  if (!manhajRange) return [];
  
  return getQuestionsByVidRange(manhajRange.from, manhajRange.to);
};

/**
 * Get questions for a specific student by NID and category
 */
export const getStudentQuestionsByCategory = (nid: string, category: string): QuranQuestion[] => {
  const student = getStudentByNid(nid);
  if (!student) return [];

  if (!student.grade) {
    console.warn(`Student ${nid} has no grade`);
    return [];
  }

  const grade = Number(student.grade);
  const manhajRange = getManhajRange(grade, category);
  
  if (!manhajRange) return [];
  
  return getQuestionsByVidRange(manhajRange.from, manhajRange.to);
};

// =============== STATISTICS & UTILITY FUNCTIONS ===============

/**
 * Get statistics about students
 */
export const getStudentStatistics = () => {
  const students = getStudents();
  const grades = getAvailableGrades();
  
  return {
    totalStudents: students.length,
    totalGrades: grades.length,
    gradeDistribution: grades.map(grade => ({
      grade,
      count: students.filter(s => s.grade === grade).length
    })),
    reading_modeDistribution: getAvailableReading_mode().map(reading_mode => ({
      reading_mode,
      count: students.filter(s => s.reading_mode?.toLowerCase() === reading_mode.toLowerCase()).length
    })),
    incompleteStudents: getStudentsWithIncompleteData().length
  };
};

/**
 * Get statistics about questions
 */
export const getQuestionStatistics = () => {
  const questions = getQuranQuestions();
  const verses = getQuranVerses();
  
  return {
    totalQuestions: questions.length,
    totalVerses: verses.length,
    totalPages: [...new Set(verses.map(v => v.page))].length,
    totalJuz: [...new Set(verses.map(v => v.juz))].length,
    totalSurahs: [...new Set(verses.map(v => v.sura_number))].length
  };
};

/**
 * Get statistics about manhaj data
 */
export const getManhajStatistics = () => {
  const manhaj = getManhajData();
  
  return {
    totalGrades: manhaj.length,
    gradeDistribution: manhaj.map(entry => ({
      grade: entry.grade,
      categories: entry.ranges.map(range => range.category),
      totalRanges: entry.ranges.length,
      ranges: entry.ranges
    })),
    categories: getAvailableCategories(),
    allRanges: manhaj.flatMap(entry => 
      entry.ranges.map(range => ({
        grade: entry.grade,
        ...range
      }))
    )
  };
};

/**
 * Clear all cached data
 */
export const clearCache = (): void => {
  const keysToRemove = ['manhaj', 'manhajStart', 'manhajEnd', 'manhajCategory'];
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

// =============== EXPORT DEFAULTS ===============
export default {
  // Data accessors
  getStudents,
  getQuranQuestions,
  getQuranVerses,
  getManhajData,
  
  // Student queries
  getStudentByNid,
  getStudentsByGrade,
  getStudentsByReading_mode,
  getStudentsByGradeAndReading_mode,
  searchStudents,
  getAvailableGrades,
  getAvailableReading_mode,
  getStudentsWithIncompleteData,
  getDetailedStudentStatistics,
  
  // Manhaj queries
  getManhajRange,
  getManhajRangesByGrade,
  getAvailableCategories,
  getCachedManhajRange,
  isValidManhajCombination,
  
  // Quran questions queries
  getQuestionsByVidRange,
  getQuestionsByPage,
  getQuestionsBySurah,
  getQuestionsByJuz,
  filterQuestions,
  
  // Quran verses queries
  getVersesByPage,
  getVerseByVid,
  getVersesBySurah,
  getVersesByJuz,
  
  // Combined queries
  getStudentWithManhaj,
  getAllStudentsWithManhaj,
  getStudentQuestions,
  getStudentQuestionsByCategory,
  
  // Statistics & utilities
  getStudentStatistics,
  getQuestionStatistics,
  getManhajStatistics,
  clearCache
};